import React, { Children, FC, ReactNode, cloneElement, isValidElement } from 'react'

import TranslatableText from './TranslatableText'
import TranslateButton from './TranslateButton'
import useUgcTranslation from '../../../../services/useUgcTranslations'

interface TranslateWrapperProps {
  sourceLang: string
  children?: ReactNode
}

/* There are three components to translate user generated content (UGC):
 *  - TranslateWrapper
 *  - TranslatableText
 *  - TranslateButton
 *
 * These components are meant for translating an arbitrary number of strings (potentially with
 * other components between them) with a single button. If you only need to translate a single
 * string with each button, and the button can go directly underneath the string, use component
 * TranslatableContent instead.
 *
 * HOW TO USE:
 *
 * Before:
 *
 * <div>
 *  {stringToTranslate1}
 *  ...
 *  {stringToTranslateN}
 * </div>
 *
 * After:
 *
 * <div>
 *  <TranslateWrapper sourceLang={'fi'}>
 *    <TranslatableText originalText={stringToTranslate1} />
 *    ...
 *    <TranslatableText originalText={stringToTranslateN} />
 *    <TranslateButton />
 *  <TranslateWrapper />
 * </div>
 *
 */

const TranslateWrapper: FC<TranslateWrapperProps> = (props) => {
  const { sourceLang, children } = props

  // Gather all texts recursively into an array
  const originalTexts: string[] = []
  const searchTranslatableTexts = (element: ReactNode) => {
    if (isValidElement(element)) {
      if (element.type === TranslatableText) {
        originalTexts.push(element.props.originalText)
      } else if (element.props.children) {
        // If the element has children, recursively visit them
        Children.map(element.props.children, searchTranslatableTexts)
      }
    }
  }

  Children.forEach(children, searchTranslatableTexts)

  // Initialize the hook with the original texts and get the actual texts to be shown (in a map)
  const {
    texts,
    translationState,
    userPrompt,
    toggleTranslation,
  } = useUgcTranslation(originalTexts, sourceLang)

  // Traverse elements again and inject the initialized translationMap to TranslatableText elements
  const traverseAndInjectTranslationMap = (element: ReactNode): ReactNode => {
    if (isValidElement(element)) {
      if (element.type === TranslatableText) {
        return cloneElement(
          element,
          { ...element.props, translationMap: texts },
        )
      } if (element.type === TranslateButton) { // Inject button with functionality and prompt
        return cloneElement(
          element,
          {
            ...element.props,
            toggleTranslation,
            userPrompt,
            translationState,
          },
        )
      } if (element.props.children) {
        // If the element has children, recursively visit them
        return cloneElement(
          element,
          element.props,
          Children.map(element.props.children, traverseAndInjectTranslationMap),
        )
      }
    }

    return element
  }

  const translatedChildren = Children.map(children, traverseAndInjectTranslationMap)

  return <>{translatedChildren}</>
}

export default TranslateWrapper
